import { useCallback } from "react";
import { useMutation, useQuery, type QueryKey } from "@tanstack/react-query";

import constant from "@/config/constant";
import useRequestToServer from "@/service/httpService";
import { apiErrorInfo } from "@/components/daylight-insight/api/apiError";

import { ApiError, errorMessage } from "./apiError";

type Method = "GET" | "POST";

/**
 * Wraps `requestToServer` so a failed response THROWS.
 *
 * ── Why this exists ────────────────────────────────────────────────────────
 * The axios response interceptor in `service/httpService.ts` RESOLVES 4xx/5xx
 * responses instead of rejecting them:
 *
 *     if (statusCode.includes(error?.response?.status))
 *       return Promise.resolve(error.response);
 *
 * which is why every hand-rolled hook had to check `response.status >= 400` by
 * hand. TanStack Query decides `isError`, retry and error propagation purely
 * from whether the `queryFn` throws — so handing it a resolved 500 would make it
 * mark the query SUCCESSFUL and cache the error payload as valid data.
 *
 * Every query and mutation in the app must go through this. Do not call
 * `requestToServer` directly from a `queryFn`.
 *
 * The token-expiry check, silent refresh and 401 retry all still live inside
 * `requestToServer` and are unchanged.
 */
export function useApiFetcher() {
  const { requestToServer } = useRequestToServer();

  return useCallback(
    async <T,>(
      url: string,
      body: unknown,
      method: Method,
      fallbackMessage: string,
    ): Promise<T> => {
      const response = await requestToServer(
        url,
        body,
        method,
        true,
        "json",
        constant.appSources.daylight,
      );

      if (!response || response.status >= 400) {
        // Reuse the existing SP-error parser: it strips the leaked
        // `EXEC [DayLight].[…]` statement off the message and flags expected
        // empty states (e.g. "no enabled metrics were found") as no-toast.
        const { message, toast } = apiErrorInfo(
          (response as { data?: { message?: string } })?.data?.message,
          fallbackMessage,
        );
        throw new ApiError(message, response?.status ?? 0, toast);
      }

      return ((response as { data?: { data?: unknown } }).data?.data ?? null) as T;
    },
    [requestToServer],
  );
}

interface ApiQueryArgs<TRaw, TData> {
  queryKey: QueryKey;
  url: string;
  /** Request body for POST; ignored (pass null) for GET. */
  body: unknown;
  method?: Method;
  /** The lazy gate. Replaces the old `params === null` idiom exactly: a
   *  disabled query fires no request and creates no cache entry. */
  enabled?: boolean;
  fallbackMessage: string;
  /** Normalizer. Runs only when the raw data changes and is memoized by
   *  TanStack — cheaper than the `useMemo` chains it replaces. */
  select?: (raw: TRaw) => TData;
  staleTime?: number;
  gcTime?: number;
  /** `{ silent: true }` suppresses the global error toast for supplementary
   *  sections that just hide themselves on failure. */
  meta?: Record<string, unknown>;
}

/**
 * The shape every migrated hook returns.
 *
 * Deliberately identical to what the hand-rolled hooks returned, so migrating a
 * hook requires NO change to any component consuming it.
 */
export interface ApiQueryResult<TData> {
  data: TData | null;
  loading: boolean;
  error: string | null;
  refetch: () => void;
  /** Escape hatch for `isFetched`, `isStale`, `isSuccess`, … */
  query: ReturnType<typeof useQuery<TData>>;
}

/**
 * One `useQuery` call, wired to this app's service layer and error conventions.
 *
 * ── State mapping (verified against the pre-migration semantics) ────────────
 *   loading → `isFetching`, NOT `isLoading`/`isPending`.
 *       The old hooks called `setLoading(true)` at request start even when
 *       stale data was still in state, and left it false whenever `params` was
 *       null. `isFetching` reproduces both: it is true for a background
 *       refetch, and false for a disabled query (`fetchStatus: 'idle'`).
 *       `isPending` would be TRUE for a disabled query with no data, which
 *       would leave skeletons up forever on every lazily-gated hook.
 *
 *   data → `data ?? null`.
 *       A new query key starts at `undefined`, so a dashboard/filter switch
 *       reads as loading with no data — exactly what the old `doneKey` guard in
 *       `useDashboardMetrics` was written to achieve, now for free.
 *
 * NOTE: `placeholderData: keepPreviousData` is deliberately NOT used anywhere.
 * It would re-introduce precisely the stale-value flash that `doneKey` (in
 * `useDashboardMetrics`) and `intervalStale` (in `AllMetricsInsight`) exist to
 * prevent.
 */
export function useApiQuery<TRaw = unknown, TData = TRaw>({
  queryKey,
  url,
  body,
  method = "POST",
  enabled = true,
  fallbackMessage,
  select,
  staleTime,
  gcTime,
  meta,
}: ApiQueryArgs<TRaw, TData>): ApiQueryResult<TData> {
  const fetcher = useApiFetcher();

  // ── Why these are spread conditionally ─────────────────────────────────────
  // `QueryClient.defaultQueryOptions` merges with a PLAIN OBJECT SPREAD:
  //
  //     { ...defaultOptions.queries, ...getQueryDefaults(key), ...options }
  //
  // so a key present with an `undefined` value OVERWRITES the default rather
  // than falling back to it. There are explicit `=== undefined` fixups for
  // `refetchOnReconnect`, `throwOnError` and `networkMode` — but NOT for
  // `staleTime` or `gcTime`.
  //
  // Passing `staleTime: undefined` therefore wiped the client's 5-minute
  // default, and `isStaleByTime(staleTime = 0)` then treats the data as
  // immediately stale — so `refetchOnMount: true` refetched on EVERY mount.
  // With `key={s.pillar}` remounting `PillarPage` on each dashboard switch,
  // that meant every pillar change refired its queries and the cache bought
  // nothing. Omitting the keys entirely is the fix.
  const query = useQuery({
    queryKey,
    queryFn: () => fetcher<TRaw>(url, body, method, fallbackMessage),
    enabled,
    ...(select !== undefined ? { select } : {}),
    ...(staleTime !== undefined ? { staleTime } : {}),
    ...(gcTime !== undefined ? { gcTime } : {}),
    ...(meta !== undefined ? { meta } : {}),
  }) as ReturnType<typeof useQuery<TData>>;

  // `query.refetch` is bound to the observer and stable across renders, so
  // depending on it (rather than on `query`, which is a fresh object every
  // render) keeps the returned `refetch` referentially stable — safe to use as
  // an effect dependency.
  const { refetch: refetchQuery } = query;
  const refetch = useCallback(() => {
    void refetchQuery();
  }, [refetchQuery]);

  return {
    data: (query.data ?? null) as TData | null,
    loading: query.isFetching,
    error: errorMessage(query.error),
    refetch,
    query,
  };
}

/**
 * A write, wired to the same fetcher (so a failed response throws rather than
 * resolving) and to the same error conventions.
 *
 * `onSuccess` is where invalidation belongs — it is the ONE place that knows a
 * write landed. Note that unlike `useQuery`, `useMutation` DOES still support
 * `onSuccess`/`onError` in v5.
 *
 * Mutations never retry (`retry: 0` in the client defaults): neither
 * `save-custom-view` nor `delete-custom-view` is idempotent.
 */
export function useApiMutation<TVariables>({
  url,
  method = "POST",
  fallbackMessage,
  buildBody,
  onSuccess,
}: {
  url: string;
  method?: Method;
  fallbackMessage: string;
  /** Turns the caller's arguments into the request body. */
  buildBody: (variables: TVariables) => unknown;
  onSuccess?: (variables: TVariables) => void;
}) {
  const fetcher = useApiFetcher();

  const mutation = useMutation({
    mutationFn: (variables: TVariables) =>
      fetcher<unknown>(url, buildBody(variables), method, fallbackMessage),
    onSuccess: (_data, variables) => onSuccess?.(variables),
  });

  /**
   * Runs the mutation and resolves to true/false instead of throwing.
   *
   * This preserves the `Promise<boolean>` contract the existing callers were
   * written against — `saveCustomView(...).then(ok => ok && refetchViews())` in
   * `tables.tsx` and `ClinicianDetailsTable.tsx` keeps working untouched.
   */
  // `mutateAsync` is stable across renders (bound to the mutation observer), so
  // `run` is stable too.
  const { mutateAsync } = mutation;
  const run = useCallback(
    async (variables: TVariables): Promise<boolean> => {
      try {
        await mutateAsync(variables);
        return true;
      } catch {
        // The mutation cache's global onError has already surfaced the message.
        return false;
      }
    },
    [mutateAsync],
  );

  return { run, mutation };
}
